import {IConnectedService, ILookupValue} from "../../../../model";
import {connectedServicesService} from "../../../../services/api/connected-services";
import {citiesService, nexParkInstanceService, notifications} from "../../../../services";
import {useState, useEffect} from "react";
import styles from './LocationConnectedServices.module.scss';
import {Checkbox, CheckboxChangeEvent, Switch, SwitchChangeEvent} from "@progress/kendo-react-inputs";
import {connectedServiceIconService} from "../../../../pages/customers/customer/connected-service-icon-service";
import {FormDropDown, LookupTextFormField} from "../../../common";
import { Field } from '@progress/kendo-react-form';
import {DropDownButton} from "@progress/kendo-react-buttons";
import {ConnectedServiceEnum} from "../../../../model/api/connected-service";
import {caretAltDownIcon} from "@progress/kendo-svg-icons";
interface LocationConnectedServicesProps {
    edit?: boolean;
    valueGetter: (name: string) => any;
    onChange: (name: string, options: { value: any }) => void;
    customerId?: number;
    onConnectedServicesChange?: (services: IConnectedService[]) => void;
}

export const LocationConnectedServices = (props: LocationConnectedServicesProps) => {
    const { edit, customerId, valueGetter, onChange, onConnectedServicesChange } = props;
    const [cities, setCities] = useState<ILookupValue[]>([]);
    const [nexParkInstances, setNexParkInstances] = useState<ILookupValue[]>([]);
    const [dictionaryServices, setDictionaryServices] = useState<IConnectedService[]>([]);
    const selectedServices: IConnectedService[] = valueGetter('connectedServices') || [];

    useEffect(() => {
        const fetchData = async () => {
            try {
                const results = await Promise.allSettled([
                    connectedServicesService.get(),
                    customerId ? citiesService.getLookupValuesByCustomer(customerId) : citiesService.getLookupValues(),
                    customerId ? nexParkInstanceService.getLookupValuesByCustomer(customerId) : nexParkInstanceService.getLookupValues()
                ]);

                const [
                    dictServicesResult,
                    citiesResult,
                    nexParkResult
                ] = results;

                if (dictServicesResult.status === 'fulfilled') {
                    const filteredDictService = dictServicesResult.value ? dictServicesResult.value['filter'](service => service.id === ConnectedServiceEnum.UmojoAnalytics) : []
                    setDictionaryServices(filteredDictService);
                } else {
                    console.error("Failed to fetch dictionary services:", dictServicesResult);
                    notifications.error("Could not load services dictionary.");
                }

                if (citiesResult.status === 'fulfilled') {
                    setCities(citiesResult.value);
                } else {
                    console.error("Failed to fetch cities:", citiesResult);
                    notifications.error("Could not load city data.");
                }

                if (nexParkResult.status === 'fulfilled') {
                    setNexParkInstances(nexParkResult.value);
                } else {
                    console.error("Failed to fetch NexPark instances:", nexParkResult);
                    notifications.error("Could not load NexPark instances.");
                }

            } catch (e) {
                console.error("An unexpected error occurred during fetch:", e);
            }
        };

        fetchData();
    }, [customerId, edit]);

    const handleServiceToggle = (serviceToToggle: IConnectedService, isChecked: boolean) => {
        let updatedServices: IConnectedService[];

        if (isChecked) {
            const newSelectedService = {
                ...serviceToToggle,
                modules: [],
            };
            updatedServices = [...selectedServices, newSelectedService];
        } else {
            updatedServices = selectedServices.filter(s => s.id !== serviceToToggle.id);
        }

        onChange('connectedServices', { value: updatedServices });
    };

    const renderAdditionalFields = (service: IConnectedService) => {

        const serviceIndex = selectedServices.findIndex(s => s.id === service.id);
        if (serviceIndex === -1) {
            return null;
        }
        const currentModuleId = selectedServices[serviceIndex].modules?.[0]?.id;

        switch (service.id) {
            case ConnectedServiceEnum.UmojoAnalytics: {
                const allModules = dictionaryServices.length > 0 ? dictionaryServices[0].modules : [];
                const selectedModuleIds = selectedServices[serviceIndex]?.modules.map(m => m.id) || [];
                const selectedModules = selectedServices[serviceIndex]?.modules || [];

                const handleModuleChange = (moduleId: number, isChecked: boolean) => {
                    const currentSelectedModules = selectedServices[serviceIndex].modules || [];
                    let newModules: ILookupValue[];

                    if (isChecked) {
                        const moduleToAdd = allModules.find(m => m.id === moduleId);
                        if (moduleToAdd) {
                            newModules = [...currentSelectedModules, moduleToAdd];
                        } else {
                            newModules = currentSelectedModules;
                        }
                    } else {
                        newModules = currentSelectedModules.filter(m => m.id !== moduleId);
                    }

                    const newSelectedServices = [...selectedServices];
                    newSelectedServices[serviceIndex] = {
                        ...newSelectedServices[serviceIndex],
                        modules: newModules,
                    };

                    onChange('connectedServices', { value: newSelectedServices });
                };

                const analyticsModuleRenderer = (itemProps: any) => {
                    const module = itemProps.item as ILookupValue;
                    const isChecked = selectedModuleIds.includes(module.id);
                    return (
                        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
                        <div className={styles.dropdownItem} onClick={(e) => e.preventDefault()}>
                            <Checkbox
                                value={isChecked}
                                onChange={(e: CheckboxChangeEvent) => handleModuleChange(module.id, e.value)}
                            />
                            <span className={styles.dropdownItemLabel}>{module.name}</span>
                        </div>
                    );
                };

                const buttonText = selectedModules.length > 0
                    ? selectedModules.map(module => module.name).join(', ')
                    : '';

                return (
                    <>
                        {allModules &&
                            <div className={styles.configArea}>
                                <DropDownButton
                                    disabled={!edit}
                                    text={buttonText}
                                    items={allModules}
                                    itemRender={analyticsModuleRenderer}
                                    svgIcon={caretAltDownIcon}
                                    iconClass={styles.caretIcon}
                                />
                            </div>}
                    </>
                );
            }
            case ConnectedServiceEnum.NexCity:
                return (
                    <>
                        {cities && <div className={styles.configArea}>
                            <Field
                                name={'city'}
                                label="City"
                                component={edit ? FormDropDown : LookupTextFormField}
                                data={cities}
                                textField="name"
                                dataItemKey="id"
                                onChange={(e: { value: ILookupValue }) => {
                                    onChange('city', { value: e.value });
                                }}
                            />
                        </div> }

                    </>
                );
            case ConnectedServiceEnum.NexPark:
                return (
                    <>
                        {nexParkInstances &&
                            <div className={styles.configArea}>
                                <Field
                                    name={'nexParkInstance'}
                                    label="NexPark"
                                    component={edit ? FormDropDown : LookupTextFormField}
                                    data={nexParkInstances}
                                    textField="name"
                                    dataItemKey="id"
                                    value={currentModuleId ? nexParkInstances.find(i => i.id === currentModuleId) : null}
                                    onChange={(e: { value: ILookupValue }) => {
                                        onChange('nexParkInstance', { value: e.value });
                                    }}
                                />
                            </div>}
                    </>

                );
            default:
                return null;
        }
    };

    return (
        <div className={styles.container}>
            {dictionaryServices && dictionaryServices.map(service => {
                const isSelected = selectedServices.some(s => s.id === service.id);

                return (
                    <div key={service.id} className={styles.serviceBox}>
                        <div className={styles.serviceHeader}>
                            <Switch
                                checked={isSelected}
                                onChange={(event: SwitchChangeEvent) => handleServiceToggle(service, event.value)}
                                disabled={!edit}
                                onLabel={""}
                                offLabel={""}
                            />
                            <div className={styles.serviceInfo}>
                                <img src={connectedServiceIconService.getServiceIcon(service.id)} alt={'logo'} />
                            </div>
                        </div>
                        {isSelected && renderAdditionalFields(service)}
                    </div>
                );
            })}
        </div>
    );
};
